<script setup lang="ts">
import { ref, watch, computed, storeToRefs } from '#imports';
import { useMediaQuery, useWindowSize } from '@vueuse/core';

import type { IDictionary } from './../types';
import { useDictionariesStore } from './../store/dictionaries';

import PaginationLetter from '@/shared/components/pagination/pagination-letter.vue';
import { BREAKPOINT } from '@/shared/constant';

import IconAngle from '@/assets/icons/icon-angle.svg';

const TEXT_LIMIT_CHARS = {
	small: 200,
	medium: 500,
	large: 1000,
};

type CardColor = 'grey';

const props = withDefaults(defineProps<{
	card: IDictionary;
	color?: CardColor;
}>(), {
	color: 'grey',
});

const emit = defineEmits<{
	(e: 'change', id: number): void;
}>();

const dictionariesStore = useDictionariesStore();
const { selectNames, selectDictionaryId } = storeToRefs(dictionariesStore);

const selectValue = ref(selectDictionaryId.value ?? selectNames.value[0].id);

watch(selectValue, (val) => {
	emit('change', Number(val));
});

const isDemoText = ref(true);

const computedClass = computed(() => ({
	'dictionary-card': true,
	'dictionary-card--open': isDemoText.value,
	[`dictionary-card--${props.color}`]: props.color,
}));

const handleToggleDescription = () => {
	isDemoText.value = !isDemoText.value;
};

const textLimit = ref(TEXT_LIMIT_CHARS.small);
const { width } = useWindowSize();

watch(width, (val: number) => {
	if (val >= BREAKPOINT.xl) {
		textLimit.value = TEXT_LIMIT_CHARS.large;
	} else if (val >= BREAKPOINT.md) {
		textLimit.value = TEXT_LIMIT_CHARS.medium;
	} else {
		textLimit.value = TEXT_LIMIT_CHARS.small;
	}
}, {
	immediate: true,
});

const isBigText = computed(() => {
	return props.card.description.length > textLimit.value;
});

const computedLimitedText = computed(() => {
	return props.card.description.slice(0, textLimit.value) + ' ...';
});

const isSmallScreen = useMediaQuery('(max-width: 767.9px)');

const computedCardId = computed(() => `dictionary-card-${props.card.id}`);
</script>

<template>
	<article :id="computedCardId" :class="computedClass">
		<div class="dictionary-card__inner">
			<div class="dictionary-card__body">
				<client-only>
					<teleport :to="`#${computedCardId}`" :disabled="isSmallScreen">
						<img
							:src="card.image ?? '/images/dictionary/001.png'"
							:alt="card.name_short"
							width="130"
							height="182"
							class="dictionary-card__image"
						>
					</teleport>
				</client-only>

				<div class="dictionary-card__description">
					<p v-if="isBigText && isDemoText" v-html="computedLimitedText" />
					<p v-else v-html="card.description" />

					<button
						v-if="isBigText"
						class="dictionary-card__description-toggle button button--icon"
						@click="handleToggleDescription"
					>
						<icon-angle class="icon dictionary-card__description-icon" />
					</button>
				</div>
			</div>

			<div
				v-if="selectNames.length > 0"
				class="dictionary-card__select"
			>
				<v-select
					v-model="selectValue"
					:options="selectNames"
					color="gray"
				/>
			</div>

			<div class="dictionary-card__action">
				<ul class="dictionary-card__action-list">
					<li class="dictionary-card__action-item">
						<nuxt-link :to="`/dictionary/${card.slug}/ot_redkollegii`" class="dictionary-card__action-link">
							ОТ&nbsp;РЕДКОЛЛЕГИИ
						</nuxt-link>
					</li>
					<li class="dictionary-card__action-item">
						<nuxt-link :to="`/dictionary/${card.slug}/ot_izdatelya`" class="dictionary-card__action-link">
							ОТ&nbsp;издателя
						</nuxt-link>
					</li>
					<li class="dictionary-card__action-item">
						<nuxt-link :to="`/dictionary/${card.slug}/avtory`" class="dictionary-card__action-link">
							Авторы
						</nuxt-link>
					</li>
					<li class="dictionary-card__action-item">
						<nuxt-link :to="`/dictionary/${card.slug}/sokrashcheniya`" class="dictionary-card__action-link">
							Сокращения
						</nuxt-link>
					</li>
				</ul>

				<pagination-letter
					:letters="card.letters"
					:dictionary="card.slug"
				/>
			</div>
		</div>
	</article>
</template>

<style lang="scss" scoped>
$min-height-mobile: 182px;
$min-height-laptop: 233px;
$width-mobile: 130px;
$width-desktop: 166px;
$gap-left-mobile: 14px;
$gap-left-desktop: 44px;
$action-gap-left-mobile: calc($width-mobile + $gap-left-mobile);
$action-gap-left-desktop: calc($width-desktop + $gap-left-desktop);

.dictionary-card {
	padding: rem($gap-medium) rem($gap-mini);

	border-radius: rem($radius-primary);
	background-color: $white;

	@media #{$screen-tablet} {
		display: flex;
		flex-direction: row-reverse;
	}

	@media #{$screen-laptop} {
		padding: rem(40px) rem($gap-average);
	}

	@media #{$screen-laptop-1} {
		padding: rem(40px) rem($gap-gigantic) rem(40px) rem($gap-average);
	}

	&--grey {
		background-color: $wild-sand;
	}

	&--open & {
		&__description-toggle {
			transform: rotate(180deg);
		}
	}

	&__inner {
		min-height: rem($min-height-mobile);

		@media #{$screen-tablet} {
			flex-grow: 1;
		}

		@media #{$screen-laptop} {
			min-height: rem($min-height-laptop);
		}
	}

	&__body {
		margin-bottom: rem($gap);

		@media #{$screen-tablet} {
			display: flex;

			margin-bottom: rem($gap-medium);
		}
	}

	&__image {
		float: left;

		width: rem(130px);
		height: rem($min-height-mobile);
		margin-right: rem($gap-left-mobile);

		object-position: center;

		border-radius: rem($radius-primary);

		@media #{$screen-tablet} {
			float: none;
		}

		@media #{$screen-laptop} {
			width: rem($width-desktop);
			height: rem($min-height-laptop);
			margin-right: rem($gap-left-desktop);
			margin-bottom: 0;
		}
	}

	&__description {
		@include text-mini(300);
		font-style: italic;

		min-height: rem($min-height-mobile);
		margin-bottom: rem($gap-medium);

		@include reset-indent-last;

		@media #{$screen-tablet} {
			@include text-base;

			min-height: initial;
		}

		&-toggle {
			$size: 32px;

			display: block;

			width: rem($size);
			height: rem($size);
			margin-right: rem($gap-small);
			margin-left: auto;

			color: $black;
		}

		&-icon {
			$size: 21px;

			width: rem($size);
			height: rem($size);

			transform: rotate(-90deg);

			color: $black;
		}
	}

	&__select {
		$max-width: 556px;

		max-width: calc($max-width + $action-gap-left-desktop);
		margin-bottom: rem($gap-medium);
	}

	&__action {
		width: 100%;

		&-list {
			display: grid;
			grid-template: repeat(2, 1fr) / repeat(2, 1fr);
			gap: rem($gap-small);

			margin-bottom: rem($gap);

			@media #{$screen-tablet} {
				display: flex;
				flex-wrap: wrap;
				gap: rem($gap-medium);
			}

			@media #{$screen-laptop} {
				margin-bottom: rem($gap-average);
			}
		}

		&-item {
			&:nth-child(1) {
				order: 1;

				@media #{$screen-tablet} {
					order: initial;
				}
			}

			&:nth-child(2) {
				order: 3;

				@media #{$screen-tablet} {
					order: initial;
				}
			}

			&:nth-child(3) {
				order: 2;

				@media #{$screen-tablet} {
					order: initial;
				}
			}

			&:nth-child(4) {
				order: 4;

				@media #{$screen-tablet} {
					order: initial;
				}
			}
		}

		&-link {
			@include text-small;

			transition: color $transition-duration $transition-function;

			letter-spacing: 0.09em;
			text-transform: uppercase;
			text-decoration: none;

			color: $malibu;

			@media #{$screen-tablet} {
				@include text-base;
			}

			@media (hover: hover) {
				&:hover {
					color: $black;
				}
			}
		}
	}
}
</style>
